import React, { useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import clsx from 'clsx';
import { X } from 'lucide-react';

/**
 * Modal primitives.
 *
 *   <Modal isOpen onClose>            — centered card dialog
 *   <SidePanel isOpen onClose>        — slide from end edge (RTL-aware via Tailwind logical inset)
 *   <BottomSheet isOpen onClose>      — slide from bottom (mobile-first)
 *
 * All share:
 *   - body scroll lock while open
 *   - Escape closes
 *   - focus-trap-friendly markup (the close button is first focusable)
 *   - on-brand backdrop, no harsh black
 */

const OVERLAY_BG = 'bg-ink/45 backdrop-blur-[3px]';

function useDialogBehavior(isOpen, onClose) {
    useEffect(() => {
        if (!isOpen) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
        document.addEventListener('keydown', onKey);
        return () => {
            document.body.style.overflow = prev;
            document.removeEventListener('keydown', onKey);
        };
    }, [isOpen, onClose]);
}

function DialogHeader({ title, subtitle, onClose, showCloseButton }) {
    if (!title && !showCloseButton) return null;
    return (
        <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-line-subtle">
            <div className="min-w-0 flex-1">
                {title && <h2 className="heading-md truncate">{title}</h2>}
                {subtitle && <p className="text-sm text-ink-muted mt-1">{subtitle}</p>}
            </div>
            {showCloseButton && (
                <button
                    type="button"
                    onClick={onClose}
                    className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-md text-ink-subtle hover:text-ink hover:bg-surface-muted transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                    aria-label="סגור"
                >
                    <X className="w-5 h-5" />
                </button>
            )}
        </div>
    );
}

const SIZES = {
    sm:   'max-w-md',
    md:   'max-w-lg',
    lg:   'max-w-2xl',
    xl:   'max-w-4xl',
    '2xl':'max-w-6xl',
    full: 'max-w-full mx-4',
};

export function Modal({
    isOpen,
    onClose,
    title,
    subtitle,
    children,
    footer,
    size = 'md',
    closeOnOverlayClick = true,
    showCloseButton = true,
    className,
    contentClassName,
    dir = 'rtl',
}) {
    useDialogBehavior(isOpen, onClose);
    const handleOverlay = useCallback(() => { if (closeOnOverlayClick) onClose?.(); }, [closeOnOverlayClick, onClose]);

    return (
        <AnimatePresence>
            {isOpen && (
                <div dir={dir} className="fixed inset-0 z-modal" role="dialog" aria-modal="true">
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.18 }}
                        className={clsx('fixed inset-0 z-modal-back', OVERLAY_BG)}
                        onClick={handleOverlay}
                    />
                    <div className="fixed inset-0 z-modal overflow-y-auto pointer-events-none">
                        <div className="min-h-full flex items-center justify-center p-4">
                            <motion.div
                                initial={{ opacity: 0, scale: 0.96, y: 12 }}
                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                exit={{ opacity: 0, scale: 0.96, y: 12 }}
                                transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
                                onClick={(e) => e.stopPropagation()}
                                className={clsx(
                                    'pointer-events-auto w-full bg-surface rounded-xl shadow-elevated border border-line-subtle overflow-hidden',
                                    SIZES[size] || SIZES.md,
                                    className,
                                )}
                            >
                                <DialogHeader title={title} subtitle={subtitle} onClose={onClose} showCloseButton={showCloseButton} />
                                <div className={clsx('px-6 py-5', contentClassName)}>{children}</div>
                                {footer && (
                                    <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-line-subtle bg-surface-muted/60">
                                        {footer}
                                    </div>
                                )}
                            </motion.div>
                        </div>
                    </div>
                </div>
            )}
        </AnimatePresence>
    );
}

const PANEL_WIDTHS = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-2xl',
};

export function SidePanel({
    isOpen,
    onClose,
    title,
    subtitle,
    children,
    footer,
    width = 'md',
    closeOnOverlayClick = true,
    showCloseButton = true,
    side = 'end',         // 'start' = left in LTR / right in RTL; 'end' = opposite
    dir = 'rtl',
}) {
    useDialogBehavior(isOpen, onClose);
    const fromX = side === 'end' ? '100%' : '-100%';

    return (
        <AnimatePresence>
            {isOpen && (
                <div dir={dir} className="fixed inset-0 z-modal" role="dialog" aria-modal="true">
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.18 }}
                        className={clsx('fixed inset-0 z-modal-back', OVERLAY_BG)}
                        onClick={closeOnOverlayClick ? onClose : undefined}
                    />
                    <motion.div
                        initial={{ x: fromX }}
                        animate={{ x: 0 }}
                        exit={{ x: fromX }}
                        transition={{ duration: 0.28, ease: [0.32, 0.72, 0, 1] }}
                        className={clsx(
                            'fixed top-0 h-full w-full bg-surface shadow-2xl z-modal overflow-y-auto flex flex-col',
                            side === 'end' ? (dir === 'rtl' ? 'left-0' : 'right-0') : (dir === 'rtl' ? 'right-0' : 'left-0'),
                            PANEL_WIDTHS[width] || PANEL_WIDTHS.md,
                        )}
                    >
                        <div className="sticky top-0 z-10 bg-surface">
                            <DialogHeader title={title} subtitle={subtitle} onClose={onClose} showCloseButton={showCloseButton} />
                        </div>
                        <div className="flex-1 px-6 py-5">{children}</div>
                        {footer && (
                            <div className="sticky bottom-0 z-10 flex items-center justify-end gap-3 px-6 py-4 border-t border-line-subtle bg-surface">
                                {footer}
                            </div>
                        )}
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
}

/**
 * BottomSheet — mobile-first. Slides up from the bottom, full-width.
 * Great for action sheets on phones.
 */
export function BottomSheet({
    isOpen,
    onClose,
    title,
    subtitle,
    children,
    footer,
    closeOnOverlayClick = true,
    showCloseButton = true,
    showHandle = true,
    dir = 'rtl',
    maxHeight = '90vh',
    className,
}) {
    useDialogBehavior(isOpen, onClose);

    return (
        <AnimatePresence>
            {isOpen && (
                <div dir={dir} className="fixed inset-0 z-modal" role="dialog" aria-modal="true">
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.18 }}
                        className={clsx('fixed inset-0 z-modal-back', OVERLAY_BG)}
                        onClick={closeOnOverlayClick ? onClose : undefined}
                    />
                    <motion.div
                        initial={{ y: '100%' }}
                        animate={{ y: 0 }}
                        exit={{ y: '100%' }}
                        transition={{ duration: 0.32, ease: [0.32, 0.72, 0, 1] }}
                        className={clsx(
                            'fixed inset-x-0 bottom-0 mx-auto w-full max-w-lg z-modal bg-surface rounded-t-3xl shadow-2xl border border-line-subtle flex flex-col',
                            className,
                        )}
                        style={{ maxHeight }}
                    >
                        {showHandle && (
                            <div className="pt-2 pb-1 flex items-center justify-center">
                                <span className="w-10 h-1.5 rounded-pill bg-line-strong" aria-hidden="true" />
                            </div>
                        )}
                        {(title || showCloseButton) && (
                            <DialogHeader title={title} subtitle={subtitle} onClose={onClose} showCloseButton={showCloseButton} />
                        )}
                        <div className="flex-1 overflow-y-auto px-6 py-5 safe-bottom">{children}</div>
                        {footer && (
                            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-line-subtle bg-surface safe-bottom">
                                {footer}
                            </div>
                        )}
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
}

export default Modal;
